import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

const initValidation = (shape: any) => {
  return yup.object().shape(shape).required();
};

// const signInSchema = initValidation({
//   email: yup.string().email().required(),
//   password: yup.string().required(),
// });

// const forgotPasswordSchema = initValidation({
//   email: yup.string().email().required(),
// });

const validationResolver = (schema: any) => yupResolver(schema);

const invalidClassName = "is-invalid";
// const validClassName = "is-valid";

export const toggleInvalidInput = (invalid: boolean) => (invalid ? invalidClassName : "");

/**
 * Masks input to allow only numbers, parentheses, and basic math operators
 *
 * This function filters out all characters except:
 * - Digits (0-9)
 * - Parentheses ( )
 * - Plus sign (+)
 * - Minus sign (-)
 *
 * @param value - Input string to mask
 * @returns Masked string with only allowed characters
 *
 * @example
 * maskOnlyNumbers("abc123def") // Returns "123"
 * maskOnlyNumbers("1+2-3(4)") // Returns "1+2-3(4)"
 * maskOnlyNumbers("phone: 555-1234") // Returns "555-1234"
 */
export const maskOnlyNumbers = (value: string) => {
  return value.replace(/[^\d()+-]/g, "");
};

/**
 * Formats and validates international phone numbers
 * Allows + at the beginning, country code, and proper formatting
 *
 * Features:
 * - Automatically adds +1 (US) prefix if missing and number looks like US format
 * - Automatically adds + prefix if missing for other numbers
 * - Removes duplicate + signs
 * - Filters out invalid characters
 * - Limits length to reasonable phone number size
 * - Preserves spaces and parentheses for formatting
 * - Handles empty values gracefully
 * - Prevents single + from being treated as valid input
 *
 * @param value - Input string to format
 * @returns Formatted phone number string with proper international format, or empty string if invalid
 *
 * @example
 * formatPhoneNumber("1234567890") // Returns "+11234567890"
 * formatPhoneNumber("615-753-4216") // Returns "+16157534216"
 * formatPhoneNumber("+1 (234) 567-890") // Returns "+1 (234) 567-890"
 * formatPhoneNumber("++1234567890") // Returns "+1234567890"
 * formatPhoneNumber("") // Returns ""
 * formatPhoneNumber("+") // Returns ""
 * formatPhoneNumber("   +   ") // Returns ""
 */
export const formatPhoneNumber = (value: string): string => {
  // If value is empty or only contains +, return empty string
  // This prevents single + from being treated as valid input
  if (!value || value.trim() === "" || value.trim() === "+") {
    return "";
  }

  // Remove all characters except digits, +, (, ), -, and spaces
  let cleanedValue: string = value.replace(/[^\d()+\-\s]/g, "");

  // Check if it looks like a US phone number (10 digits without country code)
  const digitsOnly = cleanedValue.replace(/[^\d]/g, "");
  const isUSFormat = digitsOnly.length === 10 && !cleanedValue.startsWith("+");

  // Ensure it starts with + for international format
  if (!cleanedValue.startsWith("+")) {
    if (isUSFormat) {
      // Add +1 for US numbers
      cleanedValue = `+1${cleanedValue}`;
    } else {
      // Add + for other numbers
      cleanedValue = `+${cleanedValue}`;
    }
  }

  // Remove extra + signs (keep only the first one)
  const plusCount = (cleanedValue.match(/\+/g) || []).length;
  if (plusCount > 1) {
    cleanedValue = cleanedValue.replace(/\+/g, "");
    cleanedValue = `+${cleanedValue}`;
  }

  // If after cleaning we only have +, return empty string
  // This handles cases where user only typed + and nothing else
  if (cleanedValue === "+") {
    return "";
  }

  // Limit total length to reasonable phone number length
  const maxLength = 20; // International phone numbers can be up to 15 digits + formatting
  const currentLength = cleanedValue?.length ?? 0;
  if (currentLength > maxLength) {
    cleanedValue = cleanedValue.slice(0, maxLength);
  }

  return cleanedValue;
};

/**
 * Removes all alphabetic characters from a string
 *
 * This function filters out all letters (a-z, A-Z) while preserving:
 * - Numbers (0-9)
 * - Special characters
 * - Spaces and punctuation
 *
 * @param inputString - Input string to process
 * @returns String with all letters removed
 *
 * @example
 * replaceLetters("abc123def") // Returns "123"
 * replaceLetters("Phone: 555-1234") // Returns ": 555-1234"
 * replaceLetters("Extension 123") // Returns " 123"
 */
export const replaceLetters = (inputString: string) => inputString.replace(/[a-zA-Z]/g, "");

const validationPhone = (val: string | undefined): boolean => {
  if (!val) return false;
  const clearMask = val && val.replace(/-|_|[{()}]/g, "").length;
  return (clearMask as number) > 11;
};

export { validationResolver, initValidation, validationPhone };
